import { useState, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, isDemoMode, oauthStartUrl, type OAuthProvider } from '../lib/api';

const OAUTH_ERRORS: Record<string, string> = {
  cancelled: 'Sign-in was cancelled. You can try again whenever you’re ready.',
  invalid_state: 'That sign-in attempt expired. Please try again.',
  invalid_response: 'The sign-in provider returned an invalid response. Please try again.',
  not_configured: 'That sign-in option is not available yet. Continue with email for now.',
  provider_failed: 'The sign-in provider could not verify your account. Please try again.',
  account_mismatch: 'Please use the same provider account that is connected to Piixel.',
};

export function oauthErrorMessage(code: string | null): string | null {
  if (!code) return null;
  return OAUTH_ERRORS[code] ?? 'Sign-in could not be completed. Please try again.';
}

function GitHubIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path fill="currentColor" d="M12 .7a11.5 11.5 0 0 0-3.64 22.4c.58.1.79-.25.79-.56v-2.23c-3.22.7-3.9-1.37-3.9-1.37-.52-1.34-1.28-1.7-1.28-1.7-1.05-.72.08-.7.08-.7 1.16.08 1.77 1.19 1.77 1.19 1.03 1.77 2.7 1.26 3.36.96.1-.75.4-1.26.73-1.55-2.57-.29-5.27-1.28-5.27-5.69 0-1.26.45-2.28 1.18-3.08-.12-.29-.51-1.46.11-3.04 0 0 .97-.31 3.16 1.18a10.98 10.98 0 0 1 5.75 0c2.2-1.49 3.16-1.18 3.16-1.18.63 1.58.23 2.75.12 3.04.73.8 1.17 1.82 1.17 3.08 0 4.42-2.7 5.39-5.28 5.68.42.36.79 1.07.79 2.16v3.2c0 .31.2.67.8.56A11.5 11.5 0 0 0 12 .7Z" />
    </svg>
  );
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M21.6 12.23c0-.71-.06-1.4-.18-2.07H12v3.92h5.38a4.6 4.6 0 0 1-2 3.02v2.54h3.24c1.9-1.75 2.98-4.33 2.98-7.41Z" />
      <path fill="#34A853" d="M12 22c2.7 0 4.98-.9 6.63-2.43l-3.24-2.53c-.9.6-2.05.96-3.39.96-2.61 0-4.82-1.76-5.61-4.13H3.04v2.62A10 10 0 0 0 12 22Z" />
      <path fill="#FBBC05" d="M6.39 13.87A6 6 0 0 1 6.08 12c0-.65.11-1.28.31-1.87V7.51H3.04A10 10 0 0 0 2 12c0 1.61.38 3.14 1.04 4.49l3.35-2.62Z" />
      <path fill="#EA4335" d="M12 6c1.47 0 2.79.5 3.83 1.5l2.87-2.87A9.62 9.62 0 0 0 12 2a10 10 0 0 0-8.96 5.51l3.35 2.62C7.18 7.76 9.39 6 12 6Z" />
    </svg>
  );
}

const PROVIDERS: Array<{ id: OAuthProvider; label: string; icon: () => JSX.Element }> = [
  { id: 'github', label: 'Continue with GitHub', icon: GitHubIcon },
  { id: 'google', label: 'Continue with Google', icon: GoogleIcon },
];

export default function AuthProviders({
  returnTo,
  failureTo,
  onError,
}: {
  returnTo: string;
  failureTo: string;
  onError: (message: string) => void;
}) {
  const nav = useNavigate();
  const [busy, setBusy] = useState<OAuthProvider | null>(null);

  const continueDemo = async (event: MouseEvent<HTMLAnchorElement>, provider: OAuthProvider): Promise<void> => {
    if (!isDemoMode()) return;
    event.preventDefault();
    setBusy(provider);
    try {
      await api.login('demo@piixel.ai', 'demo-password');
      nav(returnTo);
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Sign-in failed');
      setBusy(null);
    }
  };

  return (
    <div className="auth-providers" aria-label="Sign-in options">
      {PROVIDERS.map(({ id, label, icon: Icon }) => (
        <a
          key={id}
          className={`auth-provider auth-provider-${id}`}
          href={oauthStartUrl(id, returnTo, failureTo)}
          onClick={(event) => void continueDemo(event, id)}
          aria-busy={busy === id}
        >
          <Icon />
          <span>{busy === id ? 'Connecting…' : label}</span>
        </a>
      ))}
    </div>
  );
}
